import {
  Calendar,
  Clock,
  Hammer,
  Home,
  Image as ImageIcon,
  Package,
  Receipt,
  ScanLine,
  Scale,
  StickyNote,
  Target,
  Users,
} from 'lucide-react-native';
import type { ComponentType } from 'react';

import { Palette } from '@/constants/theme';

/**
 * Catalog + persistence for the Tools sheet grid (hamburger menu).
 * Wave 62: the 8 tiles are user-curated, same contract as the Quick
 * Actions sheet — saved order in `property.tools_menu_keys_json`,
 * edited at /tools-menu, defaults below.
 */

export interface ToolsMenuItem {
  key: string;
  label: string;
  /** One-liner for the editor rows. */
  sub: string;
  href: string;
  /** Soft tint for the tile's icon chip. */
  chipBg: string;
  /** Icon stroke color paired to the chip. */
  iconColor: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
}

// Chip tints — mirror the sheet's existing palette rotation.
const CHIP_MINT  = '#DCE7E1';
const CHIP_HONEY = '#F3DDAE';
const CHIP_CLAY  = 'rgba(176,95,60,0.15)';
const CHIP_SKY   = 'rgba(42,111,151,0.14)';
const CHIP_CREAM = '#F0E9D6';
const CHIP_MOSS  = 'rgba(133,162,103,0.18)';
const ICON_SKY   = '#2A6F97';

export const TOOLS_CATALOG: ToolsMenuItem[] = [
  { key: 'scan',      label: 'AI Smart Scan', sub: 'AI reads any paper — auto-filed',      href: '/scan',            chipBg: CHIP_MINT,  iconColor: Palette.forest,    Icon: ScanLine },
  { key: 'schedule',  label: 'Schedule',   sub: 'Every upcoming task',                  href: '/(tabs)/schedule', chipBg: CHIP_HONEY, iconColor: Palette.honeyDeep, Icon: Calendar },
  { key: 'systems',   label: 'Systems',    sub: 'Manage every system in the home',      href: '/systems',         chipBg: CHIP_SKY,   iconColor: ICON_SKY,          Icon: Home },
  { key: 'quotes',    label: 'Quotes',     sub: 'Bids, estimates, price history',       href: '/quotes',          chipBg: CHIP_CLAY,  iconColor: Palette.clay,      Icon: Scale },
  { key: 'expenses',  label: 'Expenses',   sub: 'Spending, rollups, receipts',          href: '/expenses',        chipBg: CHIP_MOSS,  iconColor: Palette.moss,      Icon: Receipt },
  { key: 'contacts',  label: 'Contacts',   sub: 'Your trusted pros',                    href: '/contacts',        chipBg: CHIP_CREAM, iconColor: Palette.ink,       Icon: Users },
  { key: 'goals',     label: 'Projects',   sub: 'Active projects + dream list',         href: '/projects',        chipBg: CHIP_HONEY, iconColor: Palette.honeyDeep, Icon: Target },
  { key: 'media',     label: 'Files',      sub: 'Photos, manuals, documents',           href: '/media',           chipBg: CHIP_SKY,   iconColor: ICON_SKY,          Icon: ImageIcon },
  { key: 'inventory', label: 'Inventory',  sub: 'Electronics, jewelry, tools',          href: '/inventory',       chipBg: CHIP_CLAY,  iconColor: Palette.clay,      Icon: Package },
  { key: 'notes',     label: 'Notes',      sub: 'Free-form jottings & lists',           href: '/notes',           chipBg: CHIP_MINT,  iconColor: Palette.forest,    Icon: StickyNote },
  { key: 'timeline',  label: 'Timeline',   sub: "Everything that's happened here",      href: '/timeline',        chipBg: CHIP_CREAM, iconColor: Palette.ink,       Icon: Clock },
  { key: 'pro',       label: 'Find a Pro', sub: 'Local search by trade',                href: '/pros',            chipBg: CHIP_MOSS,  iconColor: Palette.moss,      Icon: Hammer },
];

/** Default 8 — fills the 4×2 grid. Wave 62 (user call): Quotes joins,
 *  Inventory steps back (still one tap away in the editor / catalog). */
export const DEFAULT_TOOLS_KEYS: string[] = [
  'scan', 'schedule', 'systems', 'quotes', 'expenses', 'contacts', 'goals', 'media',
];

export function parseToolsKeys(raw: string | null | undefined): string[] {
  if (!raw) return DEFAULT_TOOLS_KEYS;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_TOOLS_KEYS;
    const valid = new Set(TOOLS_CATALOG.map((t) => t.key));
    const cleaned = (arr as unknown[])
      .filter((k): k is string => typeof k === 'string')
      .filter((k) => valid.has(k));
    if (cleaned.length === 0) return DEFAULT_TOOLS_KEYS;
    return cleaned;
  } catch {
    return DEFAULT_TOOLS_KEYS;
  }
}

export function getToolByKey(key: string): ToolsMenuItem | null {
  return TOOLS_CATALOG.find((t) => t.key === key) ?? null;
}
